import {
  decodeTokenToAccessToken,
  decodeTokenToIdToken,
  getZitadelRoles,
  UserDbEntry,
  XeoUser,
  ZitadelRole,
} from '@xeovision/shared';
import { User } from 'oidc-client-ts';

export const useAuthNStore = defineStore('auth-n-store', () => {
  const user = ref<User | null>(null);
  const dbEntry = ref<UserDbEntry | null>(null);
  const xeoUser: ComputedRef<XeoUser> = computed(() => {
    return {
      zitadelAccessTokenPayload: decodedAccessToken.value,
      dbEntry: dbEntry.value,
    };
  });

  const isLoggedIn = computed(() => !!user.value);
  const profile = computed(() => {
    if (!user.value) return null;
    return user.value.profile;
  });

  const accessToken = computed(() => {
    if (!user.value) {
      return null;
    }
    return user.value.access_token;
  });
  const decodedAccessToken = computed(() => {
    if (!accessToken.value) return null;
    return decodeTokenToAccessToken(accessToken.value);
  });

  const idToken = computed(() => {
    if (!user.value) return null;
    if (!user.value.id_token) return null;
    return user.value.id_token;
  });
  const decodedIdToken = computed(() => {
    if (!idToken.value) return null;
    return decodeTokenToIdToken(idToken.value);
  });

  const refreshToken = computed(() => {
    if (!user.value) return null;
    if (!user.value.refresh_token) return null;
    return user.value.refresh_token;
  });

  const userRoles = computed(() => {
    if (!decodedAccessToken.value) return [];
    return getZitadelRoles(decodedAccessToken.value);
  });

  function isRoleActive(role: ZitadelRole) {
    return userRoles.value.includes(role);
  }

  async function refreshDbEntry() {
    if (!accessToken.value) {
      dbEntry.value = null;
      return;
    }
    try {
      const { client } = useHttpClientStore();
      dbEntry.value = (await client.get<UserDbEntry>('/user/entry')).data;
    } catch (error) {
      console.error('Error fetching user entry', error);
    }
  }
  watch(accessToken, refreshDbEntry);

  function setUser(authUser: User) {
    user.value = authUser;
  }
  function clearUser() {
    user.value = null;
  }

  return {
    user,
    xeoUser,

    isLoggedIn,
    profile,

    accessToken,
    decodedAccessToken,

    idToken,
    decodedIdToken,

    refreshToken,

    userRoles,

    isRoleActive,
    refreshDbEntry,

    setUser,
    clearUser,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthNStore, import.meta.hot));
}
